import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { latestUserEhrAppointmentSelector } from '@screens/NutritionistHub/models/nutritionistHub.selectors.ts'
import { getAppointmentDetails, isAppointmentScheduled } from '@screens/NutritionistHub/utils.ts'
import { userSelector } from '@selectors/app.ts'
import { usePaidCallCreditCount } from '@src/hooks/usePaidCallCreditCount.ts'
import { useActiveVideoSubscription } from '@src/hooks/useActiveVideoSubscription.ts'
import { RootStackParamList } from '@navigation/types'
import { Feature, useFeatureFlag } from '@components'
import { UserPropertyOnboardingExperienceKind } from '@src/types'

export const usePaidCallCreditsCard = () => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()

  const latestAppointment = useSelector(latestUserEhrAppointmentSelector)
  const user = useSelector(userSelector)

  const videoCallCreditsEnabled = useFeatureFlag(Feature.VideoCallCredits)

  const appointmentDetails = latestAppointment
    ? getAppointmentDetails(latestAppointment)
    : undefined
  const appointmentScheduled = appointmentDetails && isAppointmentScheduled(appointmentDetails)
  const paidCallCreditCount = usePaidCallCreditCount()
  const { videoCallSubscription, videoCallSubscriptionProduct } = useActiveVideoSubscription()

  const onboardingExperience = user?.onboardingExperience
  const weightLossUser =
    onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.WeightLoss &&
    onboardingExperience.participating

  const props = useMemo((): {
    primaryText: string
    secondaryText: string
    handleOnPress: () => void
  } | null => {
    if (!videoCallCreditsEnabled || !videoCallSubscription) {
      return null
    }

    if (paidCallCreditCount === 0 && videoCallSubscriptionProduct?.uhc) {
      return {
        primaryText: 'You Have no Calls Left',
        secondaryText:
          'You’ll get more call credits next month to help you achieve your weight loss goal!',
        handleOnPress: () => {
          navigation.navigate('NoMoreCallsLeftModal', {
            nextBillingDate: videoCallSubscription.currentPeriodEndAt,
            recurring: videoCallSubscriptionProduct.recurring,
          })
        },
      }
    }

    if (latestAppointment && appointmentScheduled) {
      return {
        primaryText: 'Complete your next call',
        secondaryText: `Complete your next call to achieve your ${
          weightLossUser ? 'weight loss goal' : 'goals'
        }!`,
        handleOnPress: () => {
          navigation.navigate('NutritionistHub')
        },
      }
    }

    if (latestAppointment && !appointmentScheduled) {
      return {
        primaryText: 'Schedule your next call',
        secondaryText: `Schedule your next call to achieve your ${
          weightLossUser ? 'weight loss goal' : 'goals'
        }!`,
        handleOnPress: () => {
          navigation.navigate('CallScheduling', {
            appointmentKind: latestAppointment.kind,
            pendingAppointmentId: latestAppointment.id,
            allowBackNavigation: true,
          })
        },
      }
    }

    return null
  }, [
    videoCallCreditsEnabled,
    videoCallSubscription,
    paidCallCreditCount,
    latestAppointment,
    appointmentScheduled,
    videoCallSubscriptionProduct,
    navigation,
    weightLossUser,
  ])

  return {
    isVisible: !!props,
    props: props ? { paidCallCreditCount, ...props } : undefined,
  }
}
